import { useState } from "react";
import { Link } from "react-router-dom";

type Step = "details" | "consent" | "success";

interface HealingForm {
  fullName: string;
  phone: string;
  email: string;
  challenges: string;
  meditationDays: string;
  dreamCount: string;
  conceptsAware: "" | "yes" | "no";
  mentorName: string;
  mentorPhone: string;
}

const EMPTY_FORM: HealingForm = {
  fullName: "", phone: "", email: "",
  challenges: "", meditationDays: "", dreamCount: "",
  conceptsAware: "", mentorName: "", mentorPhone: "",
};

export function HealingPage() {
  const [step, setStep] = useState<Step>("details");
  const [form, setForm] = useState<HealingForm>(EMPTY_FORM);
  const [consentGiven, setConsentGiven] = useState(false);
  const [loading, setLoading] = useState(false);

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  }

  function handleStep1(e: React.FormEvent) {
    e.preventDefault();
    setStep("consent");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function handleStep2(e: React.FormEvent) {
    e.preventDefault();
    if (!consentGiven) return;
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setStep("success");
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 1000);
  }

  return (
    <>
      {/* Hero */}
      <section className="page-hero page-hero--healing">
        <div className="container">
          <p className="eyebrow">Transformative Sessions</p>
          <h1 className="display-2">Hanuman Healing</h1>
          <p className="hero__body" style={{ maxWidth: "600px", margin: "0 auto" }}>
            A one-on-one deep healing session with our master teacher.
            Experience profound transformation through ancient wisdom, energy work,
            and personalized spiritual guidance.
          </p>
          <div style={{ marginTop: "1.5rem", display: "flex", gap: "1rem", justifyContent: "center", flexWrap: "wrap" }}>
            <span className="badge badge--healing">$500 per session</span>
            <span className="badge badge--healing">Payment via Zelle</span>
            <span className="badge badge--healing">Limited Availability</span>
          </div>
        </div>
      </section>

      {/* Progress bar */}
      {step !== "success" && (
        <div className="healing-progress-bar">
          <div className="container">
            <div className="healing-progress-steps">
              <div className={`healing-step ${step === "details" ? "active" : "done"}`}>
                <div className="healing-step__num">1</div>
                <div className="healing-step__label">Session Details</div>
              </div>
              <div className="healing-step__connector" />
              <div className={`healing-step ${step === "consent" ? "active" : ""}`}>
                <div className="healing-step__num">2</div>
                <div className="healing-step__label">Consent &amp; Payment</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Step 1: Details ─────────────────────────────────── */}
      {step === "details" && (
        <section className="section">
          <div className="container container--reading">
            <div className="card">
              <h2 style={{ marginBottom: "0.5rem" }}>Step 1 — About You</h2>
              <p style={{ color: "var(--c-ink-75)", marginBottom: "1.5rem" }}>
                Help us prepare the most effective session for you.
              </p>
              <form onSubmit={handleStep1} className="healing-form">
                <div className="contact-form__row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="fullName">Full Name *</label>
                    <input id="fullName" name="fullName" type="text" className="form-input"
                      required value={form.fullName} onChange={handleChange} placeholder="Your full name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="phone">Phone *</label>
                    <input id="phone" name="phone" type="tel" className="form-input"
                      required value={form.phone} onChange={handleChange} placeholder="+1 555 000 0000" />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="email">Email</label>
                  <input id="email" name="email" type="email" className="form-input"
                    value={form.email} onChange={handleChange} placeholder="your@email.com" />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="challenges">
                    What challenges or intentions bring you to this session? *
                  </label>
                  <textarea id="challenges" name="challenges" className="form-input" rows={4}
                    required value={form.challenges} onChange={handleChange}
                    placeholder="Describe your current challenges, goals, or what you hope to heal…"
                    style={{ resize: "vertical" }} />
                </div>

                <div className="contact-form__row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="meditationDays">
                      How many days/week do you meditate?
                    </label>
                    <select id="meditationDays" name="meditationDays" className="form-input"
                      value={form.meditationDays} onChange={handleChange}>
                      <option value="">Select…</option>
                      <option value="0">0 — Just starting</option>
                      <option value="1-2">1–2 days/week</option>
                      <option value="3-4">3–4 days/week</option>
                      <option value="5-6">5–6 days/week</option>
                      <option value="7">Every day</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="dreamCount">
                      How often do you recall dreams?
                    </label>
                    <select id="dreamCount" name="dreamCount" className="form-input"
                      value={form.dreamCount} onChange={handleChange}>
                      <option value="">Select…</option>
                      <option value="rarely">Rarely</option>
                      <option value="sometimes">Sometimes (1–2/week)</option>
                      <option value="often">Often (3–5/week)</option>
                      <option value="daily">Almost every night</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">
                    Are you aware of concepts like astral projection, third eye activation, or chakra healing?
                  </label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input type="radio" name="conceptsAware" value="yes"
                        checked={form.conceptsAware === "yes"}
                        onChange={handleChange} /> Yes
                    </label>
                    <label className="radio-label">
                      <input type="radio" name="conceptsAware" value="no"
                        checked={form.conceptsAware === "no"}
                        onChange={handleChange} /> No — I'm new to these
                    </label>
                  </div>
                </div>

                <div className="healing-form__section-label">Mentor (Optional)</div>
                <p style={{ fontSize: "0.85rem", color: "var(--c-ink-75)", marginBottom: "1rem" }}>
                  If you were referred by a teacher or mentor, please share their details.
                </p>
                <div className="contact-form__row">
                  <div className="form-group">
                    <label className="form-label" htmlFor="mentorName">Mentor Name</label>
                    <input id="mentorName" name="mentorName" type="text" className="form-input"
                      value={form.mentorName} onChange={handleChange} placeholder="Mentor's name" />
                  </div>
                  <div className="form-group">
                    <label className="form-label" htmlFor="mentorPhone">Mentor Phone</label>
                    <input id="mentorPhone" name="mentorPhone" type="tel" className="form-input"
                      value={form.mentorPhone} onChange={handleChange} placeholder="Mentor's phone" />
                  </div>
                </div>

                <button type="submit" className="btn btn--brand btn--lg" style={{ width: "100%", marginTop: "0.5rem" }}>
                  Continue to Consent &amp; Payment →
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* ── Step 2: Consent + Payment ──────────────────────── */}
      {step === "consent" && (
        <section className="section">
          <div className="container container--reading">
            <div className="card">
              <h2 style={{ marginBottom: "0.5rem" }}>Step 2 — Consent &amp; Payment</h2>
              <p style={{ color: "var(--c-ink-75)", marginBottom: "1.5rem" }}>
                Please review the terms and complete payment to confirm your session.
              </p>

              {/* Session summary */}
              <div className="healing-summary-box">
                <div className="healing-summary-row">
                  <span>Participant</span>
                  <strong>{form.fullName}</strong>
                </div>
                <div className="healing-summary-row">
                  <span>Phone</span>
                  <strong>{form.phone}</strong>
                </div>
                <div className="healing-summary-row">
                  <span>Session Fee</span>
                  <strong className="healing-summary__price">$500</strong>
                </div>
                <div className="healing-summary-row">
                  <span>Payment Method</span>
                  <strong>Zelle</strong>
                </div>
              </div>

              {/* Payment instructions */}
              <div className="healing-payment-box">
                <h3 style={{ marginBottom: "0.75rem" }}>Payment Instructions</h3>
                <p style={{ marginBottom: "0.5rem" }}>
                  Send <strong>$500</strong> via Zelle to confirm your session:
                </p>
                <div className="healing-payment-detail">
                  <span>Recipient</span>
                  <strong>Aekam</strong>
                </div>
                <div className="healing-payment-detail">
                  <span>Zelle Email</span>
                  <strong>hello@aekam.app</strong>
                </div>
                <p style={{ fontSize: "0.85rem", color: "var(--c-ink-75)", marginTop: "0.75rem" }}>
                  Note: Include your name and "Healing Session" in the Zelle memo. Your session
                  will be confirmed within 24 hours after payment is received.
                </p>
              </div>

              {/* Consent */}
              <div className="healing-consent-box">
                <h3 style={{ marginBottom: "0.75rem" }}>Consent Statement</h3>
                <div className="healing-consent-text">
                  <p>
                    I understand that this is a spiritual healing session, not a substitute for 
                    medical, psychological, or psychiatric treatment. I voluntarily participate and 
                    agree that the organizers and teachers are not liable for any 
                    outcomes. I confirm that all information provided is accurate and I am 18+ years old.
                  </p>
                  <p style={{ marginTop: "0.75rem" }}>
                    Sessions are non-refundable once scheduled. Rescheduling requests must be made
                    at least 48 hours in advance.
                  </p>
                </div>
                <label className="healing-consent-check">
                  <input
                    type="checkbox"
                    checked={consentGiven}
                    onChange={(e) => setConsentGiven(e.target.checked)}
                  />
                  <span>
                    I have read and agree to the above consent statement, and I confirm I have
                    sent or will send the $500 Zelle payment before the session.
                  </span>
                </label>
              </div>

              <form onSubmit={handleStep2} style={{ display: "flex", gap: "0.75rem", marginTop: "1rem" }}>
                <button
                  type="button"
                  onClick={() => setStep("details")}
                  className="btn btn--outline btn--lg"
                  style={{ flex: 1 }}
                >
                  ← Back
                </button>
                <button
                  type="submit"
                  className="btn btn--brand btn--lg"
                  disabled={!consentGiven || loading}
                  style={{ flex: 2 }}
                >
                  {loading ? "Submitting…" : "Confirm Session Registration"}
                </button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* ── Success ────────────────────────────────────────── */}
      {step === "success" && (
        <section className="section">
          <div className="container container--reading" style={{ textAlign: "center" }}>
            <div className="card">
              <div style={{ fontSize: "3.5rem", marginBottom: "1rem" }}>🕉️</div>
              <h1 style={{ marginBottom: "0.5rem" }}>Registration Received</h1>
              <p style={{ color: "var(--c-ink-75)", marginBottom: "1rem", lineHeight: 1.7 }}>
                Thank you, <strong>{form.fullName}</strong>. Your Hanuman Healing session 
                registration has been submitted. Once your Zelle payment is confirmed,
                we'll reach out to schedule your session.
              </p>
              <div className="healing-success-detail">
                <span>📞</span> We'll contact you at <strong>{form.phone}</strong>
              </div>
              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "center", marginTop: "1.5rem", flexWrap: "wrap" }}>
                <Link to="/events" className="btn btn--brand">View Upcoming Events</Link>
                <Link to="/" className="btn btn--outline">Back to Home</Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* About section */}
      {step !== "success" && (
        <section className="section" style={{ background: "var(--c-surface-alt)" }}>
          <div className="container">
            <div style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center" }}>
              <h2 style={{ marginBottom: "1rem" }}>What is Hanuman Healing?</h2>
              <p style={{ color: "var(--c-ink-75)", lineHeight: 1.8, marginBottom: "1.5rem" }}>
                Named in honor of the infinite devotion of Hanuman, these one-on-one sessions
                combine 4P Meditation techniques, dream work, chakra activation, and personal
                guidance from our master teacher. Each session is uniquely tailored to your
                spiritual journey and life circumstances.
              </p>
              <div className="healing-benefits-grid">
                {[
                  { icon: "🔮", title: "Third Eye Activation", desc: "Open your intuitive vision and access deeper states of consciousness" },
                  { icon: "🌀", title: "Chakra Alignment", desc: "Balance your energy centers for physical and spiritual wellbeing" },
                  { icon: "🌙", title: "Dream Integration", desc: "Work with your dream wisdom to unlock healing and insight" },
                  { icon: "💫", title: "Ancestral Healing", desc: "Address patterns and energies passed down through your lineage" },
                ].map((b) => (
                  <div key={b.title} className="healing-benefit-card">
                    <div className="healing-benefit-card__icon">{b.icon}</div>
                    <h4>{b.title}</h4>
                    <p>{b.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
